import { useGetEmployerTags } from '@features/applications/hooks/useGetEmployerTags';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import React, { FC, useContext, useState } from 'react';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useGetTags } from '@features/applications/hooks/useGetTags';

interface TagsWotcFiltersProps {
  disableButtons: boolean;
  showSearchBox?: boolean;
  expended?: boolean;
  isWOTCOnly: boolean;
  selectedCompanyId?: string | number;
  selectedTag: string;
  onWotcChange: () => void;
  onTagsChange: (event: SelectChangeEvent<string>) => void;
}

const TagsWotcFilters: FC<TagsWotcFiltersProps> = ({
  onWotcChange,
  onTagsChange,
  disableButtons,
  selectedCompanyId,
  isWOTCOnly,
  showSearchBox,
  expended,
  selectedTag,
}) => {
  const isMedium = useMediaQuery('(min-width:922px)');
  const isSmall = useMediaQuery('(min-width:600px)');
  const { authData } = useContext(authContext);
  const [errorText, setErrorText] = useState<string>('');

  const { tagsData } = useGetEmployerTags({
    setErrorText,
    enabled: authData?.user?.company ? true : false,
  });
  const { tagsData: enterpriseTagsData } = useGetTags({
    selectedCompanyId,
    setErrorText,
    enabled: authData?.user?.enterprise ? true : false,
  });

  const renderTags = (tags: { id: number; tag: string }[]) =>
    tags.map((tag) => (
      <MenuItem key={tag.id} value={tag.id}>
        {tag.tag}
      </MenuItem>
    ));

  return (
    <Stack
      sx={{
        width: '100%',
        marginTop: showSearchBox ? 3 : 0,
        paddingLeft: showSearchBox ? 2 : 0,
        gap: {
          xs: 2,
          sm: 2,
          md: 0,
        },
      }}
      direction={{ sm: 'column', md: 'row' }}
      spacing={2}
      gap={isMedium ? 2 : 0}
    >
      <Button
        size="small"
        variant={isWOTCOnly ? 'contained' : 'outlined'}
        color="success"
        startIcon={<AttachMoneyIcon />}
        onClick={onWotcChange}
        fullWidth
        disabled={disableButtons}
        sx={{
          padding: expended ? '5px 30px !important' : '5px 9px !important',
          marginBottom: isMedium || isSmall ? '10px' : 'auto',
          whiteSpace: 'nowrap',
          fontSize: '15px !important',
          maxWidth: {
            lg: '123px',
            md:'100%'
          },
        }}
      >
        WOTC
      </Button>

      <Tooltip
        title={
          authData?.user?.enterprise && !selectedCompanyId
            ? `Please select at least 1 company`
            : ''
        }
        disableHoverListener={Boolean(
          authData?.user?.enterprise && selectedCompanyId,
        )}
        arrow
      >
        <FormControl
          size="small"
          variant="outlined"
          sx={{
            '& .css-6hp17o-MuiList-root-MuiMenu-list': {
              padding: '0 15px',
            },
            borderRadius: 5,
            width: '100% !important',
            marginTop: isMedium || isSmall ? '10px' : 'auto',
          }}
          disabled={Boolean(
            disableButtons ||
              (authData?.user?.enterprise && !selectedCompanyId),
          )}
          fullWidth
        >
          <InputLabel id="tag-select-label">Filter by Tag</InputLabel>
          <Select
            size="small"
            labelId="tag-select-label"
            id="tag-select"
            value={selectedTag}
            label="Filter by tag"
            onChange={(e) => {
              onTagsChange(e);
            }}
            MenuProps={{
              PaperProps: { style: { borderRadius: 5 } },
            }}
            sx={{ borderRadius: 5, fontSize: '14px' }}
          >
            <MenuItem value="All">All tags</MenuItem>
            {tagsData?.objects?.length
              ? renderTags(tagsData.objects)
              : enterpriseTagsData?.objects?.length
                ? renderTags(enterpriseTagsData.objects)
                : null}
          </Select>
        </FormControl>
      </Tooltip>
    </Stack>
  );
};

export default TagsWotcFilters;
